<template>
	<div
		class="main-daily-quest-box"
		v-if="quest"
		:class="{
			'main-daily-quest-box--completed':
				questCompleted && !questActionRequired,
		}"
	>
		<div class="main-daily-quest-box__header">
			<BoxHeaderBackground
				v-if="questCompleted"
				slug="main-daily-quest-box-header-completed"
				:gradient="{
					stopColor1: '#339C51',
					stopColor2: '#1b4d29',
				}"
			/>
			<BoxHeaderBackground v-else slug="main-daily-quest-box-header" />
			<QuestTask
				v-if="quest"
				:current="quest.progress"
				:target="getAmount(quest.objective.amount)"
				:building="hasType(quest.objective) ? quest.objective.type : ''"
				:text="translate(quest.assignment)"
				:hint="quest.hintText ? translate(quest.hintText) : ''"
				:building-level="
					hasLevel(quest.objective) ? quest.objective.level : 0
				"
				:quest-action-required="questActionRequired"
				:town="getTownName"
				:icon="hasType(quest.objective) ? quest.objective.type : ''"
				:background="false"
				isDaily
			/>
			<div class="main-daily-quest-box__header--right">
				<div class="quests-icon icon-quests-80" />
				<div class="timer-wrapper">
					<Timer
						:time="formattedRemainingTime"
						:iconSize="16"
						:fontSize="16"
						hasIcon
					/>
				</div>
			</div>
		</div>
		<!-- TODO podmienku potom napojit na spravny stav questu - pre otestovanie popupu dat `false` namiesto `quest` -->
		<div v-if="quest" class="main-daily-quest-box__content">
			<DailyQuestBoxRewards
				:quest="quest"
				:questCompleted="questCompleted && !questActionRequired"
				:background="false"
			>
				<ShrinkText
					v-if="finished"
					:text="translate('ui.claimed')"
					:fontSize="18"
					:isBold="true"
					class="claimed"
				/>
				<AppTextButton
					v-else
					class="claim-rewards-btn"
					:type="StatusTypes.SUCCESS"
					:text="translate('button.claim')"
					:pulse="true"
					:disabled="!claimRewardButtonShow"
					:notification="claimRewardButtonShow ? '!' : undefined"
					@click="handleClaimRewards"
				/>
			</DailyQuestBoxRewards>
			<slot />
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import QuestTask from "@/components/Quests/QuestTask.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import BoxHeaderBackground from "@/components/SVG/BoxHeaderBackground.vue";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { settlementNames } from "@/game/entities/config/settlements";
import { hasLevel, hasSettlementId, hasType } from "@/game/QuestRepository";
import useUserStore from "@/store/useUserStore";
import type { QuestActive } from "@/types/quest";
import { StatusTypes } from "@/types/ui";
import { getAmount } from "@/utils/number";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import Timer from "../Timer.vue";
import DailyQuestBoxRewards from "./DailyQuestBoxRewards.vue";

interface Props {
	quest: QuestActive | null;
	finished?: boolean;
}

const emit = defineEmits(["action", "pinAction", "claimRewards"]);

const { quest = null, finished = false } = defineProps<Props>();

const userStore = useUserStore();
const { accountEstablished } = storeToRefs(userStore);
const formattedRemainingTime = computed((): number => {
	if (!quest) {
		return 0;
	}

	const now = new Date();
	const nextExpirationDay = new Date();

	// Používame čisté lokálne gettre
	nextExpirationDay.setUTCHours(
		accountEstablished.value.getUTCHours(),
		accountEstablished.value.getUTCMinutes(),
		accountEstablished.value.getUTCSeconds(),
		0,
	);

	if (nextExpirationDay < now) {
		nextExpirationDay.setDate(nextExpirationDay.getDate() + 1);
	}

	return Math.floor((nextExpirationDay.getTime() - now.getTime()) / 1000);
});

const questCompleted = computed((): boolean => {
	if (!quest) {
		return false;
	}
	return quest.progress >= getAmount(quest.objective.amount);
});

const claimRewardButtonShow = computed((): boolean => {
	if (!quest) {
		return false;
	}

	if (quest?.payed === 0) {
		return false;
	} else if (quest?.payed === 1) {
		return true;
	}

	return questCompleted.value;
});

const handleClaimRewards = (): void => {
	emit("claimRewards", quest?.id);
};

const questActionRequired = computed((): boolean => {
	return (
		quest?.objective.action === QuestObjectiveActions.USE_RESOURCES &&
		quest.progress >= getAmount(quest.objective.amount) &&
		quest?.payed === 0
	);
});

const getTownName = computed((): string => {
	if (!quest || !quest.objective || !hasSettlementId(quest.objective)) {
		return "";
	}

	const actualSettlement = settlementNames.get(quest.objective.settlement_id);
	if (!actualSettlement) {
		return "";
	}

	return actualSettlement.name;
});
</script>

<style lang="scss" scoped>
.main-daily-quest-box {
	position: relative;
	gap: 0.5rem;
	box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
	border-width: 0.05rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #836e49, #bfa370);
	border-image-slice: 1;
	background-image: linear-gradient(to top, #ab8849, #f2dcb5);
	width: 100%;
	max-width: 25rem;
	height: auto;
	&.completed {
		box-shadow:
			0 0 0.5rem 0 rgba(43, 173, 80, 0.8),
			inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
		border: solid 0.05rem #2bad50;
		background-image: linear-gradient(
			0deg,
			rgba(130, 157, 0, 1) 0%,
			rgba(37, 38, 42, 1) 50%
		);
	}

	span {
		color: #372615 !important;
		font-size: 0.813rem;
		text-shadow: none !important;
	}

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 1;
		mix-blend-mode: multiply;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}

	&__content {
		@include flex-col-center;
		gap: 1rem;
		margin-bottom: 1.75rem;
		padding: 0 1.25rem 1.25rem;

		.quest-description {
			text-align: center;
		}
	}

	&__header {
		position: relative;
		@include flex-row-between;
		height: 4.75rem;

		&--right {
			position: absolute;
			top: -0.75rem;
			right: 0;
			z-index: 1;
			height: 100%;
		}
	}

	:deep(.box-header-bg) {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 4.625rem;

		.solid-bg-polygon {
			fill: rgba(145, 113, 56, 0.8) !important;
		}

		.texture-bg-polygon {
			opacity: 0.2 !important;
		}
	}

	&--completed {
		:deep(.box-header-bg) {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 4.625rem;

			.solid-bg-polygon {
				fill: rgba(27, 77, 41, 1) !important;
			}
		}
	}

	&-reward {
		@include flex-row-center;
		margin-top: 0.75rem;

		&-value {
			margin-right: 0.25rem;
			margin-bottom: 0.25rem;
			font-size: 1rem;
		}

		&:not(:last-child) {
			margin-right: 0.75rem;
		}
	}

	&-description {
		text-align: center;
	}

	.claim-rewards-btn {
		position: relative;
		z-index: 1;
		width: 8rem;
	}
	.claimed {
		justify-content: flex-end;
		height: 2.5rem;
		color: #b6dd00;
		font-weight: 800;
		font-size: 17px;
		text-shadow: 0.7px 0.7px 1px rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	:deep(.quest-task) {
		width: calc(100% - 5rem);
		height: 100%;
		overflow: hidden;

		.quest-task__header {
			.quest-task-text {
				font-size: 1.25rem;
				text-transform: uppercase;
				text-wrap: nowrap;
			}
		}
		.quest-task__content-task__header {
			.quest-task-text {
				font-size: 1rem;
			}
		}
	}

	.timer-wrapper {
		display: inline-flex;
		position: absolute;
		right: 0;
		bottom: -0.5rem;
		justify-content: center;
		align-items: flex-start;
		z-index: 1;
		mask-image: linear-gradient(
			to left,
			black 0%,
			black 20%,
			black 70%,
			transparent 98%
		);
		background-image: linear-gradient(
			to left,
			rgba(85, 0, 20, 0.7),
			rgba(85, 0, 20, 0.8),
			rgba(85, 0, 20, 1)
		);
		padding: 0 0.375rem;
		padding-left: 2.25rem;
		width: auto;
		height: 1.625rem;

		&::before,
		&::after {
			position: absolute;
			left: 0;
			z-index: 1;
			background: #cf0032;
			width: 100%;
			height: 0.063rem;
			content: "";
		}

		&::before {
			top: 0;
		}

		&::after {
			bottom: 0;
		}

		:deep(.timer__time) {
			padding-bottom: 0.125rem;
			color: #f2dcb5;
			font-size: 1rem;
		}
	}

	:deep(.daily-quest-box-rewards) {
		width: 100%;

		.rewards-label {
			color: #372615;
		}
		.price__value {
			color: #372615 !important;
		}
	}
	&.completed {
		:deep(.daily-quest-box-rewards) {
			width: 100%;

			.rewards-label {
				color: #f2dcb5;
			}
			.price__value {
				color: #f2dcb5 !important;
			}
		}
		:deep(.box-header-bg) {
			.texture-bg-polygon {
				mix-blend-mode: multiply;
			}
		}
	}
}
</style>
